import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { Snackbar, Alert, AlertColor } from "@mui/material";
import { alertStyles } from "./NotificationProvider.styles";
import { isCurioHandledError } from "@/utils";

const getUnhandledErrorMessage = (reason: unknown): string => {
  if (reason instanceof Error) return reason.message || "Ocorreu um erro inesperado.";
  if (typeof reason === "string") return reason;
  return "Ocorreu um erro inesperado.";
};

export interface NotificationType {
  message: string;
  type: AlertColor;
  duration?: number;
  isAuthError?: boolean;
}

interface NotificationContextType {
  notification: NotificationType | null;
  setNotification: (notification: NotificationType | null) => void;
  showNotification: (message: string, type?: AlertColor, duration?: number) => void;
  hideNotification: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification deve ser utilizado dentro de um NotificationProvider");
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notification, setNotification] = useState<NotificationType | null>(null);

  const showNotification = useCallback((message: string, type: AlertColor = "info", duration: number = 6000) => {
    setNotification({ message, type, duration });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification(null);
  }, []);

  const handleClose = useCallback(
    (_event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === "clickaway") {
        return;
      }
      hideNotification();
    },
    [hideNotification]
  );

  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Erros de mutations/queries do Curio já são notificados pelo mutationCache/queryCache
      // (queryClient.ts) com a mensagem correta (ex: ValidationError.detail); useCurioMutation
      // marca esses erros para evitar mostrar aqui uma segunda notificação genérica.
      if (isCurioHandledError(event.reason)) return;
      showNotification(getUnhandledErrorMessage(event.reason), "error");
    };
    const handleError = (event: ErrorEvent) => {
      if (isCurioHandledError(event.error)) return;
      showNotification(getUnhandledErrorMessage(event.error ?? event.message), "error");
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleError);
    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      window.removeEventListener("error", handleError);
    };
  }, [showNotification]);

  const contextValue: NotificationContextType = {
    notification,
    setNotification,
    showNotification,
    hideNotification
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={!!notification}
        autoHideDuration={notification?.duration || 6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={handleClose} severity={notification?.type || "info"} variant="filled" sx={alertStyles}>
          {notification?.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
